import { Deserializable } from '../protocols/deserializable';
import { BaseType } from '../base/base-type';
import { MemberRecord } from '../users/dto/member-record';
import { BankAccount } from '../../views/banking/models/bank-account';
import { PaylineAccount } from '../../views/banking/models/payline-account';
import { PaymentMethodType } from '../shared/payment-method-type';
import { PaymentMethodTypeIdEnum } from '../enum/shared/payment-method-type-id.enum';
import { IdentityTypeIdEnum } from '../enum/shared/identity-type-id.enum';
import { LifeStatusIdEnum } from '../enum/shared/life-status-id.enum';
import { MonthlyPensionAccountPaymentInstructionDesignationEnum } from './enum/monthly-pension-account-payment-instruction-designation.enum';
import { MonthlyPensionAccountPaymentInstructionDispositionEnum } from './enum/monthly-pension-account-payment-instruction-disposition.enum';
import { MonthlyPensionAccountPaymentInstructionDispositionDisplayEnum } from './enum/monthly-pension-account-payment-instruction-disposition-display.enum';
import { MonthlyPensionAccountPaymentInstructionStatusEnum } from './enum/monthly-pension-account-payment-instruction-status.enum';
import { MonthlyPensionAccountPaymentInstructionSplitReasonEnum } from './enum/monthly-pension-account-payment-instruction-split-reason.enum';
import { MonthlyPensionBenefitPhase } from './monthly-pension-benefit-phase';
import { MonthlyPensionAccountPaymentInstructionAvailableActions } from './monthly-pension-account-payment-instruction-available-actions';

export class MonthlyPensionAccountPaymentInstructions implements Deserializable {
  public id!: string;
  public startDate!: string;
  public endDate!: string | null;
  public payeeId!: number | null;
  public payee!: MemberRecord | null;
  public payeeFirstName!: string | null;
  public payeeLastName!: string | null;
  public payeeIdentityTypeId!: IdentityTypeIdEnum | null;
  public payeeLifeStatusId!: LifeStatusIdEnum | null;
  public payeeDesignationId!: MonthlyPensionAccountPaymentInstructionDesignationEnum;
  public payeeDesignation!: BaseType;
  public allocationPercentage!: number;
  public paymentAmount!: number;
  public commutedValue!: number | null;
  public dispositionId!: MonthlyPensionAccountPaymentInstructionDispositionEnum | null;
  public disposition!: BaseType | null;
  public dispositionDisplay!: MonthlyPensionAccountPaymentInstructionDispositionDisplayEnum | null;
  public statusId!: MonthlyPensionAccountPaymentInstructionStatusEnum;
  public status!: BaseType;
  public paymentMethodId!: PaymentMethodTypeIdEnum | null;
  public paymentMethod!: PaymentMethodType | null;
  public bankAccountId!: number | null;
  public bankAccount!: BankAccount | null;
  public paylineAccountId!: number | null;
  public paylineAccount!: PaylineAccount | null;
  public splitReasonId!: MonthlyPensionAccountPaymentInstructionSplitReasonEnum | null;
  public splitReason!: BaseType | null;
  public survivorPercentage!: number | null;
  public isParent!: boolean;
  public parentPaymentInstructionId!: string | null;
  public parentPayeeName!: string | null;
  public parentPayeeDesignationId!: MonthlyPensionAccountPaymentInstructionDesignationEnum | null;
  public splitEffectiveDate!: string | null;
  public retainJointLifeSurvivorship!: boolean;
  public createdDate!: string;
  public statusChangedDate!: string | null;
  public benefitSchedule!: MonthlyPensionBenefitPhase[];
  // Internal Portal only
  public statusChangedByName?: string | null;
  public availableActions?: MonthlyPensionAccountPaymentInstructionAvailableActions | null;
  // Not from API
  public payeeFullName!: string;
  // TODO(CSS-3616): backend field, once CSS-3358 defines the contract - the amount this
  // entitlement's payee will receive after the source account's guarantee period ends, for the
  // Source Account card's "$X /mo until <date> then $Y /mo" re-split display. Optional/undefined
  // until then; the card omits the "then $Y/mo" clause when it isn't set.
  public paymentAmountAfterGuaranteeEndDate?: number | null;

  public onDeserialize(): void {
    const Deserialize = window.injector.Deserialize;

    this.payeeFullName = [this.payeeFirstName, this.payeeLastName].filter(name => !!name).join(' ');

    if (this.payee) {
      this.payee = Deserialize.instanceOf(MemberRecord, this.payee);
    }

    if (this.payeeDesignation) {
      this.payeeDesignation = Deserialize.instanceOf(BaseType, this.payeeDesignation);
    }

    if (this.disposition) {
      this.disposition = Deserialize.instanceOf(BaseType, this.disposition);
    }

    if (this.status) {
      this.status = Deserialize.instanceOf(BaseType, this.status);
    }

    if (this.paymentMethod) {
      this.paymentMethod = Deserialize.instanceOf(PaymentMethodType, this.paymentMethod);
    }

    if (this.bankAccount) {
      this.bankAccount = Deserialize.instanceOf(BankAccount, this.bankAccount);
    }

    if (this.paylineAccount) {
      this.paylineAccount = Deserialize.instanceOf(PaylineAccount, this.paylineAccount);
    }

    if (this.splitReason) {
      this.splitReason = Deserialize.instanceOf(BaseType, this.splitReason);
    }

    this.benefitSchedule = Deserialize.arrayOf(MonthlyPensionBenefitPhase, this.benefitSchedule ?? []);

    if (this.availableActions) {
      this.availableActions = Deserialize.instanceOf(
        MonthlyPensionAccountPaymentInstructionAvailableActions,
        this.availableActions
      );
    }
  }

  public isSplitTarget(splitReasonId: MonthlyPensionAccountPaymentInstructionSplitReasonEnum): boolean {
    return !!this.availableActions?.splitReasons.some(
      reason => reason.splitReasonId === splitReasonId && reason.targetsThisInstruction
    );
  }
}
